import {
    Autocomplete,
    Box,
    Stack,
    SxProps,
    TextField,
    Typography,
} from '@mui/material'
import { forwardRef, KeyboardEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../context/account_context'
import { BranchInfo } from '../../context/branch_context'
import { useCoin } from '../../context/coin_context'
import {
    SearchTypeEnum,
    SystemBranchEnum,
    useConfigs,
} from '../../context/configs_context'
import { ProductInfo } from '../../context/product_context'
import { listProducts } from '../../services/product'
import {
    getProductStockInBranch,
    getNameText,
    maskCurrency,
} from '../../utils/maks'
import GenericModal from '../GenericModal'

interface Props {
    returnProducts(product: ProductInfo[]): void
    selectedProduct?: ProductInfo | null
    sx?: SxProps
    inputSx?: SxProps
    selectedBranch?: BranchInfo | null
    customLabel?: string
    size?: 'medium' | 'small'
    labelType?: 'label' | 'placeholder'
}

export const ProductSearchBar = forwardRef<HTMLInputElement, Props>(
    (
        {
            returnProducts,
            selectedProduct,
            sx,
            inputSx,
            selectedBranch,
            customLabel,
            size = 'small',
            labelType = 'label',
        },
        ref
    ) => {
        const { t } = useTranslation()
        const [products, setProducts] = useState<ProductInfo[]>([])
        const timeout = useRef<NodeJS.Timeout>()
        const { accountInfo } = useAccount()
        const { coins } = useCoin()
        const { configs } = useConfigs()
        const [value, setValue] = useState('')
        const [showProductNotFoundModal, setShowProductNotFoundModal] =
            useState(false)

        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch

        const isDigit = (value: string) => /^\d+$/.test(value)

        const eventListener = (e: KeyboardEvent) => {
            if (
                configs.searchType !== SearchTypeEnum.Barcode ||
                !isDigit(value)
            )
                return

            if (e.key === 'Enter') {
                searchProducts(value)
            }
        }

        const searchProducts = async (value: string) => {
            if (!value) return

            try {
                const res = await listProducts(
                    {
                        skip: 1,
                        take: 5,
                        search: value,
                        dontCount: true,
                        id_filial_filter:
                            selectedBranch?.stockBranchId ?? selectedBranch?.id,
                    },
                    coins
                )

                if (
                    configs.searchType === SearchTypeEnum.Barcode &&
                    isDigit(value)
                ) {
                    const foundBarcodeProduct = res.data.find(
                        (p) => p.barcodes.includes(value) || p.weight
                    )

                    if (foundBarcodeProduct) {
                        //@ts-ignore
                        ref?.current?.blur()
                        returnProducts([foundBarcodeProduct])
                    } else {
                        setShowProductNotFoundModal(true)
                    }

                    return
                }

                setProducts(res.data)
            } catch (error) {}
        }

        const canShowStock =
            accountInfo?.isMaster ||
            accountInfo?.rules.findIndex(
                (r) =>
                    r.group.toLowerCase() === 'produtos' &&
                    r.name.toLowerCase() === 'listar estoque'
            ) !== -1

        const getProductLabel = (product: ProductInfo) => {
            const nameText = getNameText(product, configs)

            const stock = canShowStock
                ? getProductStockInBranch(
                      product,
                      selectedBranch,
                      isMasterBranch
                  )
                : '---'

            const localizations = product.localizations
                ?.filter((l) => {
                    if (!isMasterBranch) return true

                    return l.branchId === selectedBranch?.id
                })
                ?.map((l) => l.value)
                .join(', ')

            return `${nameText}, ${t('precio')}: ${product.coin
                ?.symbol} ${maskCurrency(product.salePrice, product.coin)}, ${t(
                'stock'
            )}: ${stock}`.concat(
                localizations
                    ? `, ${t('localizaciones')}: ` + localizations
                    : ''
            )
        }

        const getProductRender = (props: any, product: ProductInfo) => {
            const nameText = getNameText(product, configs)

            const stock = canShowStock
                ? getProductStockInBranch(
                      product,
                      selectedBranch,
                      isMasterBranch
                  )
                : '---'

            const localizations = product.localizations
                ?.filter((l) => {
                    if (!isMasterBranch) return true

                    return l.branchId === selectedBranch?.id
                })
                ?.map((l) => l.value)
                .join(', ')

            const { key, ...optionProps } = props

            return (
                <Box
                    sx={{
                        py: '5px',
                        px: '7.5px',
                        '&:hover': {
                            background: 'rgba(0, 0, 0, 0.04)',
                        },
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                    component="li"
                    key={key}
                    {...optionProps}
                >
                    <Stack direction={'row'} spacing={2}>
                        <Stack direction={'column'}>
                            <Typography fontSize={'1rem'} fontWeight={'500'}>
                                {nameText}{' '}
                                <Box component={'b'}>
                                    ({t('Stock')}: {stock})
                                </Box>
                            </Typography>
                            <Typography
                                fontSize={'1rem'}
                                fontWeight={'600'}
                            ></Typography>
                            <Stack
                                direction={'row'}
                                spacing={1}
                                alignItems={'center'}
                                flexWrap={'wrap'}
                            >
                                <Typography
                                    fontSize={'0.875rem'}
                                    fontWeight={700}
                                    color={'primary.main'}
                                >
                                    {product.coin?.symbol}{' '}
                                    {maskCurrency(
                                        product.salePrice,
                                        product.coin
                                    )}
                                </Typography>
                                {localizations && (
                                    <Typography
                                        fontSize={'0.875rem'}
                                        color={'#71797E'}
                                    >
                                        {t('localizaciones')}: {localizations}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>
            )
        }

        return (
            <>
                <GenericModal
                    open={showProductNotFoundModal}
                    handleClose={setShowProductNotFoundModal}
                    title="Producto no encontrado"
                    handleSubmit={() => {
                        setShowProductNotFoundModal(false)
                    }}
                    textButton="Confirmar"
                    enableKeyboardHandler
                >
                    <Box width={'350px'} textAlign={'left'}>
                        {t(
                            'No se encontraron productos con el código de barras. Regístrese si es necesario.'
                        )}
                    </Box>
                </GenericModal>
                <Autocomplete
                    options={products}
                    filterOptions={(p) => p}
                    getOptionLabel={getProductLabel}
                    renderOption={getProductRender}
                    sx={{ width: '100%', ...sx }}
                    value={selectedProduct ?? null}
                    blurOnSelect={true}
                    onChange={(_, newProduct) => {
                        returnProducts(newProduct ? [newProduct] : [])
                    }}
                    onFocus={() => {
                        setProducts([])
                        setValue('')
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            value={value}
                            onChange={(e) => {
                                const { value } = e.target
                                setValue(value)

                                if (
                                    configs.searchType ===
                                        SearchTypeEnum.Normal ||
                                    !isDigit(value)
                                ) {
                                    clearTimeout(timeout.current)
                                    timeout.current = setTimeout(() => {
                                        searchProducts(value)
                                    }, 250)
                                }
                            }}
                            inputRef={ref}
                            onKeyDown={eventListener}
                            label={
                                labelType === 'label'
                                    ? customLabel
                                        ? t(customLabel)
                                        : t('Buscar producto')
                                    : undefined
                            }
                            placeholder={
                                labelType === 'placeholder'
                                    ? customLabel
                                        ? t(customLabel)
                                        : t('Buscar producto')
                                    : undefined
                            }
                            InputProps={{
                                ...params.InputProps,
                                sx: inputSx,
                            }}
                            sx={{ width: '100%' }}
                            size={size}
                        />
                    )}
                />
            </>
        )
    }
)
